import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedRolesList } from "../services/roleService";

interface IUseFetchRoles {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: number;
  pSize: number;
  searchTerm?: string;
  isActive?: string;
}

const usePaginatedFetchRolesList = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  isActive = "true",
}: IUseFetchRoles) => {
  const [rolesData, setRolesData] = useState<any>({});
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result = await getPaginatedRolesList({
          page,
          pSize,
          searchTerm: searchQuery || searchTerm,
          isActive,
          signal: abortControllerRef.current.signal,
        });
        setRolesData(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setRolesData({});
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, pSize, searchTerm, isActive, setIsLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [rolesData, fetchData];
};

export default usePaginatedFetchRolesList;
